export type CampaignPolicy = "EternalHero" | "Legacy" | "Mortal";

export type AttentionPolicy =
  | "backgroundSafe"
  | "queueForPresentation"
  | "forbiddenDuringCatchUp";

export type SceneMode =
  | "town"
  | "atlas"
  | "travel"
  | "dungeon"
  | "battle"
  | "training"
  | "discovery"
  | "camp"
  | "chronicle";

export type HeroValue = "curiosity" | "loyalty" | "mercy" | "courage";

export type ActorInstinctContext = "road" | "ordinaryCombat" | "direCombat" | "millerCombat" | "sharedOpeningCombat" | "repartee";

export type ActorInstinctCondition =
  | "actor-low-health"
  | "bounded-finish"
  | "hero-curious"
  | "hero-courageous"
  | "hero-merciful";

export type ActorInstinctSelector =
  | "finishing-action"
  | "restorative"
  | "guard"
  | "control-ability"
  | "unpracticed-ability"
  | "strongest-attack"
  | "basic-attack"
  | "companion-flour-veil"
  | "companion-millstone-drag"
  | "millrace-reversal"
  | "witnessed-playful-reply"
  | "unknown-route"
  | "dangerous-route"
  | "town-route"
  | "visible-dungeon-objective"
  | "mapped-opportunity"
  | "training"
  | "recovery"
  | "any";

export type ActorInstinctReasonCode =
  | "finish-safely"
  | "survive-danger"
  | "control-conflict"
  | "test-technique"
  | "meet-danger"
  | "seek-safety"
  | "explore-unknown"
  | "help-settlement"
  | "pursue-visible-objective"
  | "pursue-mapped-reward"
  | "practice-growth"
  | "continue-purposefully"
  | "protect-companion"
  | "control-tempo";

export interface ActorInstinctRule {
  id: string;
  conditions: readonly ActorInstinctCondition[];
  selector: ActorInstinctSelector;
  reasonCode: ActorInstinctReasonCode;
}

export interface ActorInstinctProfile {
  id: ActorInstinctContext;
  rules: readonly ActorInstinctRule[];
}

export interface ActorDecisionConsideration {
  commandId: string;
  actionLabel: string;
  targetLabel: string | null;
  matchedRuleId: string;
}

export interface ActorDecisionTrace {
  actorId: string;
  actorName: string;
  context: ActorInstinctContext;
  profileId: ActorInstinctContext;
  matchedRuleId: string;
  reasonCode: ActorInstinctReasonCode;
  forwardMotionReason?: ForwardMotionReason;
  considered: readonly ActorDecisionConsideration[];
  selected: ActorDecisionConsideration;
  reasons: readonly string[];
}

export type FidelityTier = "canonicalNamed" | "supporting" | "aggregate" | "ephemeral";

export type ThresholdBehavior =
  | "continue"
  | "stopBeforeNamedThreshold"
  | "forbiddenDuringCatchUp";

export type EventAggregation = "none" | "coalesce" | "summarize";

export interface EventPolicy {
  attention: AttentionPolicy;
  reversible: boolean;
  maximumFidelityAffected: FidelityTier;
  thresholdBehavior: ThresholdBehavior;
  maximumCreditedDurationTicks: number;
  aggregation: EventAggregation;
  queuedFallback: string;
}

export interface WallClockObservation {
  id: string;
  observedAtMs: number;
  elapsedMs: number;
  requestedTicks: number;
  creditedTicks: number;
  appliedTicks: number;
  stoppedAtEventId?: string;
}

export interface LifecycleState {
  policyVersion: 2;
  simulationTick: number;
  worldClockMinutes: number;
  attentionClock: number;
  presentationTimeMs: number;
  maximumCatchUpTicks: number;
  wallClockJournal: readonly WallClockObservation[];
}

export type ForwardMotionReason =
  | "explore-unseen"
  | "avoid-immediate-reverse"
  | "only-open-road"
  | "least-recent"
  | "companion-oath"
  | "companion-return";

export interface DirectedJourneyLeg {
  fromLocationId: string;
  toLocationId: string;
  plannedTick: number;
  arrivedTick: number;
  reason: ForwardMotionReason;
}

export interface RouteDirective {
  reason: ForwardMotionReason;
  destinationId: string;
  plannedTick: number;
}

export interface ForwardMotionState {
  schemaVersion: 1;
  recentLocationIds: readonly string[];
  recentLegs: readonly DirectedJourneyLeg[];
  decisionsSinceProgress: number;
  lastProgressTick: number;
  activeDirective: RouteDirective | null;
}

export interface HeroState {
  id: string;
  name: string;
  level: number;
  mastery: number;
  experience: number;
  health: number;
  maxHealth: number;
  gold: number;
  values: readonly HeroValue[];
}

export type ChampionQualification = "earned" | "adopted";

export interface ChampionEquipmentRecord {
  itemId: string;
  itemName: string;
  slot: EquipmentSlot;
  rarity: ItemState["rarity"];
}

export interface ChampionAbilityRecord {
  abilityId: string;
  abilityName: string;
  kind: AbilityKind;
  effect: AbilityEffect;
  level: number;
}

export interface ChampionInduction {
  schemaVersion: 1;
  id: string;
  contentHash: string;
  sourceCampaignId: string;
  heroId: string;
  heroName: string;
  className: string;
  level: number;
  experience: number;
  recordedTick: number;
  qualification: ChampionQualification;
  sourceCommandId: string | null;
  sourceCommandType: RecordedDepthCommandType | "unknown-released-save";
  totalCompletedQuests: number;
  equipment: readonly ChampionEquipmentRecord[];
  abilities: readonly ChampionAbilityRecord[];
}

export interface LegendCard {
  schemaVersion: 1;
  id: string;
  contentHash: string;
  sourceChampionId: string;
  sourceChampionHash: string;
  sourceCampaignId: string;
  sourceHeroId: string;
  heroName: string;
  className: string;
  level: 1_000;
  qualification: ChampionQualification;
  recordedTick: number;
  signatureAbility: ChampionAbilityRecord | null;
}

export type LegacyManifestationKind = "mortal-mentor";

export interface LegacyAppearanceFact {
  schemaVersion: 1;
  id: string;
  legendId: string;
  sourceChampionId: string;
  kind: LegacyManifestationKind;
  tick: number;
  locationId: string;
  sourceCommandId: string;
  scheduledTownVisit: number;
  townVisitOrdinal: number;
}

export interface LegacyMeetingFact {
  schemaVersion: 1;
  id: string;
  appearanceId: string;
  legendId: string;
  heroId: string;
  tick: number;
  interaction: "witnessed-demonstration";
}

export type LegacyBelief = "believes-champion-claim" | "withholds-judgment";

export interface LegacyRecognitionFact {
  schemaVersion: 1;
  id: string;
  meetingId: string;
  appearanceId: string;
  legendId: string;
  heroId: string;
  tick: number;
  recognition: "introduced-by-name";
  belief: LegacyBelief;
}

export interface LegacyLessonFact {
  schemaVersion: 1;
  id: string;
  meetingId: string;
  appearanceId: string;
  legendId: string;
  heroId: string;
  tick: number;
  abilityId: string;
  abilityName: string;
  abilityLevelAtLesson: number;
  practice: "rehearsed-existing-art";
  importedPower: false;
}

export type LegacyMentorMechanicalEffect = "none";

export interface LegacyMentorPromiseFact {
  schemaVersion: 1;
  id: string;
  meetingId: string;
  legendId: string;
  heroId: string;
  tick: number;
  locationId: string;
  sourceCommandId: string;
  scheduledTownVisit: number;
  townVisitOrdinal: number;
  relationship: "promised-return";
  promise: "return-after-next-quest";
  completedQuestBaseline: number;
  importedPower: false;
  mechanicalEffect: LegacyMentorMechanicalEffect;
}

export interface LegacyMentorReturnFact {
  schemaVersion: 1;
  id: string;
  promiseId: string;
  legendId: string;
  heroId: string;
  tick: number;
  locationId: string;
  sourceCommandId: string;
  scheduledTownVisit: number;
  townVisitOrdinal: number;
  relationship: "promise-kept";
  completedQuestBaseline: number;
  completedQuestCount: number;
  importedPower: false;
  mechanicalEffect: LegacyMentorMechanicalEffect;
}

export interface LegacyMentorFarewellFact {
  schemaVersion: 1;
  id: string;
  returnId: string;
  legendId: string;
  heroId: string;
  tick: number;
  locationId: string;
  sourceCommandId: string;
  scheduledTownVisit: number;
  townVisitOrdinal: number;
  relationship: "parted-as-friends";
  importedPower: false;
  mechanicalEffect: LegacyMentorMechanicalEffect;
}

export interface LegacyMentorMemoryFact {
  schemaVersion: 1;
  id: string;
  farewellId: string;
  legendId: string;
  heroId: string;
  recordedTick: number;
  locationId: string;
  memory: "kept-road-promise";
  importedPower: false;
  mechanicalEffect: LegacyMentorMechanicalEffect;
}

export interface LegacyMentorArcState {
  schemaVersion: 1;
  legendId: string;
  appearanceId: string;
  meetingId: string;
  heroId: string;
  promiseFact: LegacyMentorPromiseFact | null;
  returnFact: LegacyMentorReturnFact | null;
  farewellFact: LegacyMentorFarewellFact | null;
  memoryFact: LegacyMentorMemoryFact | null;
}

export interface CampaignLegacyState {
  schemaVersion: 1;
  selectorVersion: 1;
  cards: readonly LegendCard[];
}

export interface LegacyManifestationState {
  schemaVersion: 2;
  scheduleVersion: 1;
  townVisitBaseline: number;
  appearances: readonly LegacyAppearanceFact[];
  meetings: readonly LegacyMeetingFact[];
  recognitions: readonly LegacyRecognitionFact[];
  lessons: readonly LegacyLessonFact[];
  mentorArc: LegacyMentorArcState | null;
}

export interface SceneState {
  mode: SceneMode;
  location: string;
  headline: string;
  action: string;
  goal: string;
  consequence: string;
  sensoryIntensity: 0 | 1 | 2 | 3;
}

export interface ChronicleEntry extends SceneState {
  id: string;
  tick: number;
  attention: AttentionPolicy;
  consideredActions: readonly string[];
  chosenAction: string;
  rationale: string;
  policy: EventPolicy;
  commandId?: string;
  commandType?: RecordedDepthCommandType;
  consideredCommandIds?: readonly string[];
  decisionTrace?: ActorDecisionTrace;
}

export interface PendingAttentionEvent {
  id: string;
  tick: number;
  mode: SceneMode;
  location: string;
  goal: string;
  reason: string;
  policy: EventPolicy;
  commandId?: string;
  commandType?: RecordedDepthCommandType;
}

export type RecordedDepthCommandType = DepthCommand["type"] | "progress-objective";

export const recordedDepthCommandTypes: readonly RecordedDepthCommandType[] = [
  "use-dungeon-tonic",
  "reunite-companion",
  "share-companion-credit",
  "recruit-companion",
  "farewell-companion",
  "restock-tonic",
  "buy-disarming-kit",
  "buy-road-rations",
  "sell-spare-gear",
  "prepare-road-supper",
  "plan-route",
  "travel",
  "visit-town",
  "enter-dungeon",
  "invoke-dungeon-shrine",
  "move-dungeon",
  "search-dungeon",
  "open-dungeon-passage",
  "choose-pennywise-gate",
  "pass-pennywise-gate",
  "start-smithy-job",
  "smithy-stroke",
  "start-inn-bluff",
  "resolve-inn-bluff",
  "disarm-dungeon-trap",
  "unlock-dungeon-gate",
  "start-combat",
  "start-dungeon-guardian",
  "combat-action",
  "start-counter-duel",
  "counter-duel-action",
  "read-book",
  "read-useful-book",
  "practice-useful-reply",
  "start-room-challenge",
  "answer-room-challenge",
  "start-repartee",
  "repartee-action",
  "recall-repartee",
  "start-bell",
  "roll-bell",
  "move-bell",
  "admit-deferred-secret",
  "train-ability",
  "certify-weapon-technique",
  "progress-objective",
  "fulfill-quest",
  "apply-quest-reward",
  "admit-successor-quest",
  "wait",
];

export interface WorldState {
  schemaVersion: 9;
  campaignId: string;
  campaignPolicy: CampaignPolicy;
  seed: string;
  tick: number;
  hero: HeroState;
  scene: SceneState;
  chronicle: readonly ChronicleEntry[];
  lifecycle: LifecycleState;
  forwardMotion: ForwardMotionState;
  pendingAttention: readonly PendingAttentionEvent[];
  championInduction: ChampionInduction | null;
  legacy: CampaignLegacyState;
  legacyManifestations: LegacyManifestationState;
  depth: DepthState;
}

export interface Opportunity {
  mode: SceneMode;
  location: string;
  goal: string;
  candidates: readonly DepthCommandCandidate[];
  forwardMotionReason: ForwardMotionReason | null;
}

export interface ActorChoice {
  commandId: string;
  command: DepthCommand;
  action: string;
  consideredCommandIds: readonly string[];
  consideredActions: readonly string[];
  rationale: string;
  trace: ActorDecisionTrace;
}
import type { AbilityEffect, AbilityKind, DepthCommand, DepthCommandCandidate, DepthState, EquipmentSlot, ItemState } from "../depth/types";
